import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Fluid } from 'primeng/fluid';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Dialog } from 'primeng/dialog';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { PrimeTemplate } from 'primeng/api';
import { SiteContentService } from '@/app/services/site-content.service';
import { SocialLink } from '@/lib-backend';

@Component({
    selector: 'app-website-info-social',
    standalone: true,
    imports: [
        FormsModule, Button, InputText, InputNumber, Fluid,
        Select, TableModule, Dialog, Tooltip, Tag, PrimeTemplate,
    ],
    templateUrl: './social-tab.html',
})
export class SocialTab {
    readonly realmId = input.required<string>();

    protected readonly siteContentService = inject(SiteContentService);

    static readonly SOCIAL_PLATFORMS = [
        { value: 'instagram', label: 'Instagram' },
        { value: 'twitter', label: 'X (Twitter)' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'telegram', label: 'Telegram' },
        { value: 'discord', label: 'Discord' },
    ];

    protected availableSocialPlatforms = computed(() => {
        const existing = new Set(this.siteContentService.socialLinks().map(l => l.Platform));
        return SocialTab.SOCIAL_PLATFORMS.filter(p => !existing.has(p.value));
    });

    // CRUD dialog
    protected dialogVisible = signal(false);
    protected dialogMode = signal<'edit' | 'add'>('add');
    protected editIndex = signal(-1);
    protected editPlatform = signal('');
    protected editUrl = signal('');
    protected editDisplayOrder = signal<number | null>(null);
    protected saving = signal(false);
    protected saveError = signal<string | null>(null);

    // Delete dialog
    protected deleteDialogVisible = signal(false);
    protected deleteIndex = signal(-1);

    protected openAddDialog(): void {
        const available = this.availableSocialPlatforms();
        if (available.length === 0) return;
        this.dialogMode.set('add');
        this.editIndex.set(-1);
        this.editPlatform.set(available[0].value);
        this.editUrl.set('');
        this.editDisplayOrder.set(null);
        this.saveError.set(null);
        this.dialogVisible.set(true);
    }

    protected openEditDialog(index: number): void {
        const link = this.siteContentService.socialLinks()[index];
        if (!link) return;
        this.dialogMode.set('edit');
        this.editIndex.set(index);
        this.editPlatform.set(link.Platform);
        this.editUrl.set(link.Url);
        this.editDisplayOrder.set(link.DisplayOrder);
        this.saveError.set(null);
        this.dialogVisible.set(true);
    }

    protected async saveLink(): Promise<void> {
        this.saving.set(true);
        this.saveError.set(null);
        try {
            const current = [...this.siteContentService.socialLinks()];
            const entry: SocialLink = {
                Platform: this.editPlatform(),
                Url: this.editUrl(),
                DisplayOrder: this.editDisplayOrder(),
            };
            if (this.dialogMode() === 'add') {
                current.push(entry);
            } else {
                current[this.editIndex()] = entry;
            }
            const ok = await this.siteContentService.saveSocialLinks(this.realmId(), current);
            if (!ok) {
                this.saveError.set('Save failed');
                return;
            }
            this.dialogVisible.set(false);
        } finally {
            this.saving.set(false);
        }
    }

    protected confirmDelete(index: number): void {
        this.deleteIndex.set(index);
        this.deleteDialogVisible.set(true);
    }

    protected async executeDelete(): Promise<void> {
        const index = this.deleteIndex();
        const current = this.siteContentService.socialLinks().filter((_, i) => i !== index);
        await this.siteContentService.saveSocialLinks(this.realmId(), current);
        this.deleteDialogVisible.set(false);
    }
}
